import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { CSVLink } from 'react-csv';
import './Employeehr.css';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';
import { fireAudit } from '../utils/auditClient';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
const Employeehr = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useUser();
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [centers, setCenters] = useState({});
  const [centersFetched, setCentersFetched] = useState(false);
  const [employeeAcademyIds, setEmployeeAcademyIds] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [serverResponse, setServerResponse] = useState('');
  const fileInputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const token = localStorage.getItem('auth-token');
  const navigate = useNavigate();

  const headers = [
    { label: 'Name', key: 'name' },
    { label: 'DOB', key: 'dob' },
    { label: 'Gender', key: 'gender' },
    { label: 'Mobile Number', key: 'mobile_number' },
    { label: 'Employee Code', key: 'employee_code' },
    { label: 'Department', key: 'department_name' },
    { label: 'Role', key: 'role_name' },
    { label: 'Academy List', key: 'academy_list' },
  ];

  const fetchCenters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL_FEEDBACK_FETCH, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.data?.success && response.data?.data?.academyNameMap) {
        setCenters(response.data.data.academyNameMap);
        setCentersFetched(true);
      }
    } catch (error) {
      console.error('Error fetching centers:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to fetch centers data',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const fetchEmployees = async () => {
    if (!centersFetched) return;
    try {
      setError(null);
      const url = process.env.REACT_APP_API_EMPLOYEE_LIST_HR;
      const res = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        }
      });
      const data = res?.data;
      if (data?.success) {
        const employeeData = data?.data?.employees;
        const formattedEmp = employeeData.map((e) => {
          let academyNames = [];
          if (Array.isArray(e.academyIds)) {
            if (e.academyIds.length === centers.length) {
              academyNames = ["All"];
            } else {
              academyNames = e.academyIds.map(id => centers[id] || id);
            }
          } else {
            academyNames = ["-"];
          }
          return {
            id: e.id,
            employeeUserId: e.employeeUserId,
            role_name: e.roleName || '-',
            department_name: e.departmentName || '-',
            name: e.name || '-',
            mobile_number: e.mobileNumber || '-',
            employee_code: e.employeeCode || '-',
            dob: e.dob || '-',
            gender: e.gender || 'UNKNOWN',
            academy_list: academyNames.join(', ') || "",
          }
        });
        setEmployees(formattedEmp);
      }
    } catch (err) {
      console.log("Error: ", err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenters();
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [centersFetched]);

  const handleItemsPerPageChange = (eventOrValue) => {
    const value = typeof eventOrValue === 'number' ? eventOrValue : Number(eventOrValue.target.value);
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const filteredEmployees = employees.filter((emp) =>
    Object.values(emp).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;

    const sorted = [...data].sort((a, b) => {
      if (key === 'dob') {
        const aDate = new Date(a[key]);
        const bDate = new Date(b[key]);
        return direction === 'ascending' ? aDate - bDate : bDate - aDate;
      }
      const aValue = (a[key] ?? '').toString().toLowerCase();
      const bValue = (b[key] ?? '').toString().toLowerCase();
      if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedData = sortData(filteredEmployees);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );

  const totalEntries = filteredEmployees.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    const name = selectedFile ? selectedFile.name : '';
    setFileName(name);
    setServerResponse('');

    if (selectedFile) {
      const ext = name.split('.').pop()?.toLowerCase();
      if (ext !== 'xlsx') {
        Swal.fire({
          icon: 'info',
          title: 'Invalid file',
          text: 'Please select an Excel .xlsx file.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        setFile(null);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const uploadXLSX = async () => {
    if (!file) {
      setServerResponse('Please select an Excel (.xlsx) file.');
      return;
    }

    const formData = new FormData();

    const safeName = file.name.endsWith('.xlsx') ? file.name : `${file.name}.xlsx`;
    formData.append('file', file, safeName);
    try {
      setUploading(true);
      const url = process.env.REACT_APP_EMPLOYEE_API_CSV;
      const res = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
      });

      const payload = res?.data;
      console.log(res);
      let message = 'File processed successfully!';
      if (res.data?.failed > 0) {
        message = `Processed successfully! However, ${res.data.failed} row(s) were not inserted.`;
      }
      const summary = (() => {
        if (typeof payload === 'string') return payload;
        const total = payload?.total ?? 0;
        const success = payload?.success ?? 0;
        const failed = payload?.failed ?? 0;
        return `Processed ${total} row(s). Success: ${success}, Failed: ${failed}.`;
      })();
      setServerResponse(summary);

      fireAudit({
        entity: 'employee_xlsx_upload',
        affected_id: null,
        action: 'create',
        section: 'HR',
        subSection: 'Import',
        businessApi: url,
        businessMethod: 'POST',
        oldPayload: {},
        newPayload: { fileName: safeName },
        extra: { page: 'Employees', size: file.size, type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });

      setShowModal(false);
      setFile(null);
      setFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchEmployees();

      Swal.fire({
        icon: 'success',
        title: 'Upload Complete',
        text: message,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000,
      });
    } catch (error) {
      setServerResponse('Error: ' + (error?.response?.data || error.message));
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: 'There was an error processing the Excel file.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleClose = () => {
    setShowModal(false);
    setFile(null);
    setFileName('');
    setServerResponse('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setShowModal(false);
      handleClose();
    }
  };

  useEffect(() => {
    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);

  const handleNewEmployee = (event) => {
    const url = `/employeeCreation`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handleEditEmployee = (event, employeeId) => {
    const url = `/employeeCreation?employeeId=${employeeId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const downloadExcel = async () => {
    console.log("Downloading Excel...");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_DOWNLOAD_EXCEL}`,
        {
          responseType: "blob",
          headers: {
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          },
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "employee-data-template.xlsx";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Excel file downloaded successfully.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.response?.data?.message || error.message || "Error downloading Excel file.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleDeleteEmployee = (employeeId) => {

    Swal.fire({
      title: 'Delete this employee?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_EMPLOYEE_DELETE}?employeeId=${employeeId}`,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
              },
            }
          );

          if (response.data?.success) {
            setEmployees((prev) =>
              prev.filter(
                (e) => String(e.id) !== String(employeeId)
              )
            );
            Swal.fire({
              icon: 'success',
              title: 'Deleted Successfully!',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error!',
              text: response.data?.success_message || 'Failed to delete.',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          }
        } catch (err) {
          console.error('Employee Delete API error:', err.response || err.message);
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: err?.response?.data?.message || 'Something went wrong.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      }
    });
  };

  return (
    <div className="enrollment-container">
      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>

        <div style={{ display: "flex", gap: "20px", alignItems: "center" }} >
          <CSVLink
            data={filteredEmployees}
            headers={headers}
            filename="Employees.csv"
            className="btn btn-primary no-decoration"
          >
            <button className="csv_button" type="button">
              <span className="button__text">Export CSV</span>
              <span className="button__icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                  <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                  <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                  <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
                </svg>
              </span>
            </button>
          </CSVLink>

          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary no-decoration csv_button"
          >
            <span className='button__text' >Upload XLSX</span>
            <span className="button__icon">
              <svg
                className="svg"
                viewBox="0 0 20 25"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ fill: "none", paddingRight: "5px" }}
              >
                <path d="M13.5 3H8C6.343 3 5 4.343 5 6V18C5 19.657 6.343 21 8 21H12" />
                <path d="M13.5 3L19 8.625" />
                <path d="M13.5 3V7.625C13.5 8.177 13.948 8.625 14.5 8.625H19" />
                <path d="M19 8.625V11.812" />
                <path d="M17.5 21V15" />
                <path d="M17.5 15L20 17.5" />
                <path d="M17.5 15L15 17.5" />
              </svg>
            </span>

          </button>
        </div>
      </div>

      <div className="items-container">
        <select
          className="items-per-page-select"
          value={rowsPerPage}
          onChange={handleItemsPerPageChange}
        >
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange(option)}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <button className="tt-action-btn" onClick={fetchEmployees}>
            <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
            Refresh
          </button>
          {hasPrivilege(userData, 'HR', 'EMPLOYEE_LIST_MODIFY') && (
          <button className="create-button-new" onClick={handleNewEmployee} >
            <span className="create-button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23">
                <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="create-button__label">Add Employee</span>
          </button>
          )}
          {showModal && (
            <div style={styles.modal}>
              <div ref={modalRef} style={styles.modalContent}>
                <h2>Upload Excel File</h2>
                <button
                  onClick={downloadExcel}
                  style={{
                    backgroundColor: "#4CAF50",
                    color: "white",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  Download Excel
                </button>
                <input
                  id="employee-file"
                  type="file"
                  accept=".xlsx" 
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                <div className="enquiry-container">
                  <div className="enquiry-header" onClick={handleUploadButtonClick}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 10V9C7 6.23858 9.23858 4 12 4C14.7614 4 17 6.23858 17 9V10C19.2091 10 21 11.7909 21 14C21 15.4806 20.1956 16.8084 19 17.5M7 10C4.79086 10 3 11.7909 3 14C3 15.4806 3.8044 16.8084 5 17.5M7 10C7.43285 10 7.84965 10.0688 8.24006 10.1959M12 12V21M12 12L15 15M12 12L9 15"
                        stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      </path>
                    </svg>
                    <p>Choose File</p>
                  </div>
                  {fileName && <p style={{ marginTop: "10px", fontSize: "14px" }}>📂 {fileName}</p>}

                  <button
                    onClick={uploadXLSX} 
                    className="enquiry-footer"
                    disabled={uploading}
                  >
                    {uploading ? "Uploading…" : "Upload"}
                  </button>
                </div>

                <p id="response" style={styles.response}>{serverResponse}</p>

                <button onClick={handleClose} style={styles.closeButton}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {
        loading && (
          <div className="premium-loader">
            <div className="progress-track">
              <div className="progress-bar"></div>
            </div>
            <div className="status-text">
              <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
            </div>
          </div>
        )
      }
      {error && <div className="error-message">{error}</div>}
      {
        !loading && !error && filteredEmployees.length === 0 && (
          <div className="error-message">No data available</div>
        )
      }

      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
                    {hasPrivilege(userData, 'HR', 'EMPLOYEE_LIST_MODIFY') && (
              <th>Actions</th>
                    )}
            </tr>
          </thead>
          {!loading && !error && filteredEmployees.length > 0 &&
            <tbody>
              {currentRows.map((emp, idx) => (
                <tr key={emp.id || emp.employeeUserId || emp.employeeCode || idx}>
                  <td>{emp.name}</td>
                  <td>{emp.dob || '-'}</td>
                  <td>{emp.gender.toUpperCase()}</td>
                  <td>{emp.mobile_number}</td>
                  <td>{emp.employee_code}</td>
                  <td>{emp.department_name}</td>
                  <td>{emp.role_name}</td>
                  <td className="academy-dropdown-cell">
                    {(() => {
                      if (!emp.academy_list || emp.academy_list.trim() === '') {
                        return <span>-</span>;
                      }

                      const academies = emp.academy_list.split(', ').filter(Boolean);

                      if (academies.length === Object.keys(centers).length) {
                        return <span>All</span>;
                      }
                      return (
                        <details>
                          <summary>View Academies</summary>
                          <div className="academy-dropdown-list">
                            {academies.map((academy, index) => (
                              <div key={index} className="academy-name">
                                {academy}
                              </div>
                            ))}
                          </div>
                        </details>
                      );
                    })()}
                  </td>
                  {hasPrivilege(userData, 'HR', 'EMPLOYEE_LIST_MODIFY') && (
                  <td className="action-buttons">
                    <button className="edit-btn tooltip-btn" onClick={(e) => handleEditEmployee(e, emp.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                      </svg>
                      <span className="tooltip-text">Edit</span>
                    </button>
                    <button className="delete-btn tooltip-btn" onClick={() => handleDeleteEmployee(emp.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                      </svg>
                      <span className="tooltip-text">Delete</span>
                    </button>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          }
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div >
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px' },
  button: { padding: '10px 20px', margin: '10px', cursor: 'pointer' },
  response: { marginTop: '20px', fontSize: '14px', color: 'green' },
  fileName: { marginTop: '10px', fontSize: '16px', fontWeight: 'bold' },
  modal: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
  },
  modalContent: { backgroundColor: 'white', padding: '30px', borderRadius: '10px', textAlign: 'center', width: '400px' },
  closeButton: { backgroundColor: 'red', color: 'white', border: 'none', padding: '10px 20px', cursor: 'pointer' },
};

export default Employeehr;
